import * as React from 'react';
import { TolgeeProvider, useTolgeeSSR } from '@tolgee/react';

import { DEFAULT_LANGUAGE } from '~/shared/constants/i18n';
import { tolgee } from '~/shared/lib/tolgee';

type TolgeeContextData = {
    currentLanguage: string;
    updateCurrentLanguage: (newLanguage: string) => void;
};

type TolgeeProviderProps = {
    children: React.ReactNode;
    initialLanguage?: string;
};

const TolgeeContext = React.createContext<TolgeeContextData>({} as TolgeeContextData);

export function TolgeeContextProvider({ children, initialLanguage }: TolgeeProviderProps) {
    const [currentLanguage, setCurrentLanguage] = React.useState<string>(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('currentLanguage') || initialLanguage || DEFAULT_LANGUAGE;
        }
        return initialLanguage || DEFAULT_LANGUAGE;
    });

    const ssrTolgee = useTolgeeSSR(tolgee, currentLanguage);

    const updateCurrentLanguage = React.useCallback((newLanguage: string) => {
        setCurrentLanguage(newLanguage);

        if (typeof window !== 'undefined') {
            localStorage.setItem('currentLanguage', newLanguage);
        }
    }, []);

    const memoizedValue = React.useMemo(
        () => ({
            currentLanguage,
            updateCurrentLanguage,
        }),
        [currentLanguage, updateCurrentLanguage],
    );

    return (
        <TolgeeContext.Provider value={memoizedValue}>
            <TolgeeProvider tolgee={ssrTolgee}>{children}</TolgeeProvider>
        </TolgeeContext.Provider>
    );
}

export function useTolgeeContext() {
    const context = React.useContext(TolgeeContext);

    if (!context) {
        throw new Error('useTolgeeContext must be used within a TolgeeContextProvider');
    }

    return context;
}
